import { css } from '@emotion/react';
import type { EmotionJSX } from '@emotion/react/types/jsx-namespace';
import { Box, Flex, Title, VisuallyHidden, desktopFirstMediaQuery } from '@inflearn/ds-react';
import { rem } from '@mantine/core';
import type { ParseKeys } from 'i18next';
import { useTranslation } from 'next-i18next';
import { Fragment, type ReactNode, Suspense } from 'react';
import { useIsWebview } from '../../../hooks/useIsWebview';
import { DeferredComponent } from '../../util/DeferredComponent';
import AppShellLayoutForCSR from '../AppShellLayoutForCSR';
import type { CustomHeadProps } from '../CustomHead';
import CustomHead from '../CustomHead';
import { DefaultFallback } from '../DefaultFallback';
import { DefaultGuard } from '../DefaultLayout';
import { PolicyTabs } from './PolicyTabs';

interface PolicyLayoutProps {
  children: ReactNode;
  meta: CustomHeadProps & { title: ParseKeys; description: ParseKeys };
  guard?: ({ children }: { children: ReactNode }) => EmotionJSX.Element;
  isWebview?: boolean;
}

const PolicyLayout = ({
  guard: Guard = DefaultGuard,
  children,
  meta,
  isWebview: initialIsWebview,
}: PolicyLayoutProps) => {
  const { t } = useTranslation();
  const isWebview = useIsWebview({ initialIsWebview });

  const AppShellOrWebviewFragment = isWebview ? Fragment : AppShellLayoutForCSR;

  return (
    <Guard>
      <CustomHead
        {...meta}
        title={t(meta.title)}
        siteTitle={t('인프런')}
        description={t(meta.description)}
        pageCategory={t('메타.고객센터.페이지카테고리')}
      />
      <AppShellOrWebviewFragment requiredLogin={false} showChanneltalk>
        <Box
          component="main"
          mih="100vh"
          mx={32}
          css={{
            [desktopFirstMediaQuery.mobile]: {
              margin: `0 ${rem(16)}`,
            },
          }}
        >
          <VisuallyHidden>
            <Title order={1}>{meta?.title || t('인프런')}</Title>
          </VisuallyHidden>
          <Suspense
            fallback={
              <DeferredComponent>
                <DefaultFallback mih={800} />
              </DeferredComponent>
            }
          >
            <Flex
              direction="column"
              w="100%"
              my={0}
              mx="auto"
              maw={700}
              css={styleMainContentWrapper}
            >
              {!isWebview && <PolicyTabs />}
              <Box py={32}>{children}</Box>
            </Flex>
          </Suspense>
        </Box>
      </AppShellOrWebviewFragment>
    </Guard>
  );
};

const styleMainContentWrapper = css({
  flex: '1 1 0',

  [desktopFirstMediaQuery.tablet]: {
    maxWidth: rem(716),
  },
});

export default PolicyLayout;
